import { motion } from 'framer-motion';
import { DollarSign, ArrowRight, Landmark, Handshake, ShieldCheck } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Link } from 'react-router-dom';

const fundingHighlights = [
  {
    icon: Landmark,
    title: 'Multiple Sources',
    description: 'Bank, VC, Angel Investor & Alternative Financing'
  },
  {
    icon: Handshake,
    title: 'Targeted Matching',
    description: 'Kami carikan investor yang cocok untuk bisnis Anda'
  },
  {
    icon: ShieldCheck,
    title: 'Bankable Structure',
    description: 'Rapikan struktur keuangan agar investable'
  }
];

export function FundingPromo() {
  const { ref, isInView } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section ref={ref} id="funding" className="relative min-h-[80vh] flex items-center overflow-hidden">
      {/* Video Background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover object-center z-0"
      >
        <source src="/funding-bg.mp4" type="video/mp4" />
      </video>
      
      {/* Dark Navy Blue Overlay */}
      <div className="absolute inset-0 bg-[#0a1628]/85 z-[1]" />
      
      <div className="max-w-7xl mx-auto section-padding relative z-[2] py-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600/30 rounded-full mb-8">
              <DollarSign className="w-4 h-4 text-primary-400" />
              <span className="text-primary-400 text-sm font-medium">Pendanaan Bisnis</span>
            </div>

            <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-8 leading-tight">
              Butuh <span className="text-primary-400">Pendanaan</span><br />untuk Bisnis Anda?
            </h2>

            <p className="text-white/80 text-xl mb-10 leading-relaxed max-w-xl">
              Kami bantu Anda mendapatkan akses pendanaan yang tepat — dari bank, investor, 
              maupun alternatif financing. Kami juga menyiapkan struktur keuangan agar bisnis 
              Anda lebih <span className="whitespace-nowrap"><strong className="text-primary-400">bankable</strong> dan <strong className="text-primary-400">investable</strong></span>.
            </p>

            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/pendanaan-bisnis"
                className="inline-flex items-center gap-2 px-10 py-5 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30 text-lg"
              >
                <DollarSign className="w-6 h-6" />
                Ajukan Pendanaan
                <ArrowRight className="w-6 h-6" />
              </Link>
            </motion.div>
          </motion.div>

          {/* Right Content - Highlights */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6"
          >
            {fundingHighlights.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                className="glass rounded-2xl p-8 flex items-start gap-5"
              >
                <div className="w-14 h-14 rounded-xl bg-primary-600/20 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-7 h-7 text-primary-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-white/70 text-base">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
